import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import DefaultButton from 'components/Utils/NewDefaultButton';
import SubscriptionUtils from 'services/SubscriptionUtils';
import { api } from 'careand-redux/api/ApiThunks';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';

// The preference questions a patient answers to join the cancellation list, and
// the short labels used when flattening their answers into the stored
// `information` string. Defined here (the shared form) so both the booking flow
// and Carey reuse the same source of truth. Re-exported by SlotSchedule for the
// existing staff-side import.
export const CANCELLATION_LIST_QUESTIONS = [
  {
    id: 'practitioner',
    label: 'Which practitioner would you like to see?',
    required: true,
    showForUnsubscribed: false,
    options: [
      { value: 'Any practitioner', label: 'Any practitioner (most flexible)', flexible: true },
      { value: 'Primary practitioner only', label: 'My primary practitioner only' },
    ],
  },
  {
    id: 'location',
    label: 'Which location works for you?',
    required: true,
    location: true,
    showForUnsubscribed: true,
    options: [
      { value: 'Any location', label: 'Any location (most flexible)', flexible: true },
      { value: 'This clinic only', label: 'This clinic only' },
    ],
  },
  {
    id: 'type',
    label: 'What type of appointment?',
    required: true,
    showForUnsubscribed: false,
    options: [
      {
        value: 'In-person or virtual',
        label: 'In-person or virtual (most flexible)',
        flexible: true,
      },
      { value: 'In-person only', label: 'In-person only' },
      { value: 'Virtual only', label: 'Virtual only' },
    ],
  },
  {
    id: 'time',
    label: 'What time of day works best?',
    required: true,
    showForUnsubscribed: true,
    options: [
      { value: 'Any time', label: 'Any time (most flexible)', flexible: true },
      { value: 'Morning (before 12pm)', label: 'Morning (before 12pm)' },
      { value: 'Afternoon (12pm-5pm)', label: 'Afternoon (12pm-5pm)' },
    ],
  },
  {
    id: 'notes',
    label: 'Additional notes (optional)',
    required: false,
    showForUnsubscribed: true,
    type: 'textarea',
  },
];

export const FIELD_LABELS = {
  practitioner: 'Practitioner',
  location: 'Location',
  type: 'Type',
  time: 'Time',
  notes: 'Notes',
};

/**
 * Patient-facing "join the cancellation list" form: renders the preference
 * questions (filtered by subscription) and submits to the cancellation list.
 * The PARENT owns the surrounding modal/visibility — this only renders the
 * fields + Cancel/Submit and calls onCancel / onSubmitted.
 *
 * Used inline in the booking flow (when no slots are available) and surfaced by
 * Carey via an artifact launcher. `appointment` is optional context (the slot
 * search the patient was on); `extraInfo` is a short string prepended to the
 * stored information (Carey passes what the patient is waiting for).
 */
export const CancellationListForm = ({
  patient,
  appointment,
  extraInfo,
  onCancel,
  onSubmitted,
}) => {
  const dispatch = useAppDispatch();
  const [responses, setResponses] = useState({});

  const subscription = useAppSelector((state) => state.payments.subscriptions[patient?.id]);
  const isUnsubscribed = SubscriptionUtils.isUnsubscribed(subscription);
  const isLoading = useIsLoading(api.Patients.submitToCancellationList);

  const visibleQuestions = CANCELLATION_LIST_QUESTIONS.filter(
    (q) => !isUnsubscribed || q.showForUnsubscribed,
  );

  function handleResponseChange(questionId, value) {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
  }

  const isFormValid = visibleQuestions.filter((q) => q.required).every((q) => responses[q.id]);

  async function onSubmit() {
    const answers = Object.entries(responses)
      .filter(([key, value]) => value && key !== 'notes')
      .map(([key, value]) => `${FIELD_LABELS[key]}: ${value}`)
      .concat(responses.notes ? [`Notes: ${responses.notes}`] : []);
    const information = (extraInfo ? [extraInfo, ...answers] : answers).join(' | ');

    const patientListItem = {
      patient_id: patient.id,
      date: moment().unix(),
      information,
      ...(appointment ? { appointment } : {}),
    };

    await dispatch(api.Patients.submitToCancellationList({ id: patient.id, patientListItem }), {
      title: 'Join Cancellation List',
      successText: 'You have been added to the cancellation list!',
    });

    if (onSubmitted) onSubmitted();
  }

  const fieldStyle = {
    width: '100%',
    padding: '10px',
    border: '1px solid var(--color-border-default)',
    borderRadius: '4px',
    fontSize: '14px',
    fontFamily: 'inherit',
  };

  return (
    <div className="cancellation-list-form">
      <div className="explanation mb-3">
        <p>
          Can't find a suitable appointment time? Join our cancellation list and we'll reach out
          when slots become available. The more flexible you are, the sooner we can find you an
          appointment!
        </p>
      </div>
      {visibleQuestions.map((question) => (
        <div key={question.id} className="form-group mb-3">
          <label className="form-label" style={{ display: 'block', marginBottom: '8px' }}>
            {question.label} {question.required && '*'}
          </label>
          {question.type === 'textarea' ? (
            <textarea
              className="form-control"
              rows={3}
              value={responses[question.id] || ''}
              onChange={(e) => handleResponseChange(question.id, e.target.value)}
              placeholder="Any other preferences or constraints we should know about?"
              style={fieldStyle}
            />
          ) : (
            <select
              className="form-control"
              value={responses[question.id] || ''}
              onChange={(e) => handleResponseChange(question.id, e.target.value)}
              style={fieldStyle}
            >
              <option value="">Select preference</option>
              {question.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </div>
      ))}
      <div className="button-group mt-3" style={{ display: 'flex', gap: '10px' }}>
        <DefaultButton className="light" name="Cancel" onClick={onCancel} style={{ flex: 1 }} />
        <DefaultButton
          className="blue"
          name="Submit"
          onClick={onSubmit}
          disabled={!isFormValid}
          loading={isLoading}
          style={{ flex: 1 }}
        />
      </div>
    </div>
  );
};

CancellationListForm.propTypes = {
  patient: PropTypes.object.isRequired,
  appointment: PropTypes.object,
  extraInfo: PropTypes.string,
  onCancel: PropTypes.func,
  onSubmitted: PropTypes.func,
};

export default CancellationListForm;
